import React, { useEffect, useState, useRef } from 'react';
import { AlertPreset, AlertRule, POWER_MARKET_ZONES, POWER_MODELS, POWER_VARIABLES } from '../../hooks/useAlertRules';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { cn } from '../ui/Button';
import { Clock, Globe, Activity, BarChart2, AlertCircle, Hash, Info, ChevronDown, ChevronLeft, ChevronRight, Check, X, Calendar, Repeat, Settings2, Search, Layout, Pin, Star, ArrowUpDown, TrendingUp, TrendingDown } from 'lucide-react';
import { useWorkspaces } from '../../hooks/useWorkspaces';
const LOCATION_GROUPS: {
  label: string;
  locations: string[];
}[] = [{
  label: 'Markets',
  locations: ['Nordic', 'Iberian Peninsula', 'Central Eastern Europe', 'Balkans']
}, {
  label: 'Countries',
  locations: ['Germany', 'United Kingdom', 'France', 'Spain', 'Italy', 'Netherlands', 'Poland', 'Sweden', 'Norway', 'Belgium', 'Denmark', 'Austria', 'Switzerland', 'Portugal', 'Finland']
}, {
  label: 'Cities',
  locations: ['Berlin', 'London', 'Paris', 'Madrid', 'Rome', 'Amsterdam', 'Warsaw', 'Stockholm', 'Oslo', 'Munich', 'Hamburg', 'Copenhagen', 'Vienna', 'Zurich', 'Brussels']
}, {
  label: 'Offshore',
  locations: ['North Sea', 'Baltic Sea', 'Mediterranean']
}];
const ALL_LOCATIONS = LOCATION_GROUPS.flatMap(g => g.locations);
const LOCATIONS = ALL_LOCATIONS;
const MODELS = ['EPT-2', 'EPT-2 Early', 'EPT-2 HRRR', 'EPT-2e', 'EC-IFS', 'EC AIFS', 'GFS', 'ICON', 'EPEX Spot'];
const VARIABLE_THRESHOLDS: Record<string, {
  values: number[];
  unit: string;
  operator: string;
  operator_phrase: string;
}> = {
  'Wind Speed': {
    values: [2, 4, 6, 8, 10, 12, 15, 20, 25, 30],
    unit: 'm/s',
    operator: '>',
    operator_phrase: 'above'
  },
  Temperature: {
    values: [-10, -5, 0, 5, 10, 15, 20, 25, 30, 35, 40],
    unit: '°C',
    operator: '<',
    operator_phrase: 'below'
  },
  'Solar Irradiance': {
    values: [50, 100, 200, 300, 400, 500, 600, 700, 800, 1000],
    unit: 'W/m²',
    operator: '>',
    operator_phrase: 'above'
  }
};
const MODEL_VARIABLES: Record<string, string[]> = {
  'EPT-2': ['Wind Speed', 'Temperature', 'Solar Irradiance'],
  'EPT-2 Early': ['Wind Speed', 'Temperature', 'Solar Irradiance'],
  'EPT-2 HRRR': ['Wind Speed', 'Temperature'],
  'EPT-2e': ['Wind Speed', 'Temperature', 'Solar Irradiance'],
  'EC-IFS': ['Wind Speed', 'Temperature', 'Solar Irradiance'],
  'EC AIFS': ['Wind Speed', 'Temperature', 'Solar Irradiance'],
  GFS: ['Wind Speed', 'Temperature'],
  ICON: ['Wind Speed', 'Temperature']
};
function getAvailableVariables(selectedModels: string[]): string[] {
  if (!selectedModels || selectedModels.length === 0) {
    return Object.keys(VARIABLE_THRESHOLDS);
  }
  const varSet = new Set<string>();
  selectedModels.forEach(model => {
    const vars = MODEL_VARIABLES[model];
    if (vars) vars.forEach(v => varSet.add(v));
  });
  return varSet.size > 0 ? Array.from(varSet) : Object.keys(VARIABLE_THRESHOLDS);
}
interface AlertBuilderProps {
  preset: AlertPreset;
  rule: Partial<AlertRule>;
  onChange: (updates: Partial<AlertRule>) => void;
}
// Reusable locations dropdown content
function LocationsDropdownContent({
  details,
  updateDetails,
  locationSearch,
  setLocationSearch
}: {
  details: any;
  updateDetails: (u: any) => void;
  locationSearch: string;
  setLocationSearch: (s: string) => void;
}) {
  return <div className="absolute z-50 top-full mt-1 left-0 w-64 rounded-xl bg-[#1E293B] border border-slate-700/60 shadow-xl shadow-black/40 overflow-hidden" data-id="element-233">
      <div className="p-2 border-b border-slate-700/40" data-id="element-234">
        <div className="relative" data-id="element-235">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-236" />
          <input autoFocus value={locationSearch} onChange={e => setLocationSearch(e.target.value)} placeholder="Search locations…" className="w-full h-8 pl-8 pr-3 rounded-lg bg-slate-900/60 border border-slate-700/40 text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-slate-600" data-id="element-237" />
        </div>
      </div>
      {(details.locations?.length || 0) > 0 && !locationSearch && <button onClick={() => updateDetails({
      locations: []
    })} className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.06] transition-colors border-b border-slate-700/40 text-slate-500" data-id="element-238">
          <X size={12} data-id="element-239" />
          <span data-id="element-240">Clear selection</span>
        </button>}
      <div className="max-h-64 overflow-y-auto py-1" data-id="element-241">
        {LOCATION_GROUPS.map(group => {
        const filtered = locationSearch ? group.locations.filter(l => l.toLowerCase().includes(locationSearch.toLowerCase())) : group.locations;
        if (filtered.length === 0) return null;
        return <div key={group.label} data-id="element-242">
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-243">
                {group.label}
              </div>
              {filtered.map(loc => {
            const selected = (details.locations || []).includes(loc);
            return <button key={loc} onClick={() => {
              const current = details.locations || [];
              updateDetails({
                locations: selected ? current.filter((l: string) => l !== loc) : [...current, loc]
              });
            }} className="w-full flex items-center gap-3 px-3 py-1.5 text-left text-sm hover:bg-white/[0.06] transition-colors" data-id="element-244">
                    <div className={cn('w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors', selected ? 'bg-blue-500 border-blue-500' : 'border-slate-600 bg-transparent')} data-id="element-245">
                      {selected && <Check size={10} className="text-white" data-id="element-246" />}
                    </div>
                    <span className={selected ? 'text-white' : 'text-slate-400'} data-id="element-247">
                      {loc}
                    </span>
                  </button>;
          })}
            </div>;
      })}
        {locationSearch && LOCATION_GROUPS.every(g => g.locations.every(l => !l.toLowerCase().includes(locationSearch.toLowerCase()))) && <div className="px-3 py-4 text-center text-xs text-slate-500" data-id="element-248">
              No locations match "{locationSearch}"
            </div>}
      </div>
    </div>;
}
export function AlertBuilder({
  preset,
  rule,
  onChange
}: AlertBuilderProps) {
  const {
    workspaces
  } = useWorkspaces();
  const [locationsOpen, setLocationsOpen] = useState(false);
  const [modelsOpen, setModelsOpen] = useState(false);
  const [modelScopeOpen, setModelScopeOpen] = useState(false);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [locationSearch, setLocationSearch] = useState('');
  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] = useState(false);
  const [correctionTriggerOpen, setCorrectionTriggerOpen] = useState(false);
  const locationsRef = useRef<HTMLDivElement>(null);
  const modelsRef = useRef<HTMLDivElement>(null);
  const modelScopeRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const workspaceDropdownRef = useRef<HTMLDivElement>(null);
  const correctionTriggerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (locationsRef.current && !locationsRef.current.contains(e.target as Node)) {
        setLocationsOpen(false);
        setLocationSearch('');
      }
      if (modelsRef.current && !modelsRef.current.contains(e.target as Node)) {
        setModelsOpen(false);
      }
      if (modelScopeRef.current && !modelScopeRef.current.contains(e.target as Node)) {
        setModelScopeOpen(false);
      }
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setTriggerOpen(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setCalendarOpen(false);
      }
      if (workspaceDropdownRef.current && !workspaceDropdownRef.current.contains(e.target as Node)) {
        setWorkspaceDropdownOpen(false);
      }
      if (correctionTriggerRef.current && !correctionTriggerRef.current.contains(e.target as Node)) {
        setCorrectionTriggerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  useEffect(() => {
    if (!rule.rule_details) {
      onChange({
        rule_details: {
          ...preset.default_details
        },
        notification_type: preset.notification_type
      });
    }
  }, [preset, rule.rule_details, onChange]);
  const details = rule.rule_details || {};
  const updateDetails = (updates: any) => {
    onChange({
      rule_details: {
        ...details,
        ...updates
      }
    });
  };
  const updateCondition = (index: number, updates: any) => {
    const conditions = [...(details.conditions || [])];
    if (!conditions[index]) return;
    conditions[index] = {
      ...conditions[index],
      ...updates
    };
    updateDetails({
      conditions
    });
  };
  // Shared models dropdown for threshold/correction
  const renderModelsDropdown = (withVariableReset: boolean) => <div ref={modelsRef} className="relative" data-id="element-249">
      <button onClick={() => setModelsOpen(!modelsOpen)} className="w-full h-10 pl-10 pr-8 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-left text-slate-100 focus:outline-none focus:ring-2 focus:ring-pink-500/30 transition-colors hover:border-slate-600" data-id="element-250">
        {(details.models?.length || 0) > 0 ? `${details.models!.length} model${details.models!.length > 1 ? 's' : ''}` : 'All models'}
      </button>
      <BarChart2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-251" />
      <ChevronDown size={14} className={cn('absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform', modelsOpen && 'rotate-180')} data-id="element-252" />
      {modelsOpen && <div className="absolute z-50 top-full mt-1 left-0 right-0 rounded-xl bg-[#1E293B] border border-slate-700/60 shadow-xl shadow-black/40 overflow-hidden" data-id="element-253">
          <div className="max-h-48 overflow-y-auto py-1" data-id="element-254">
            {(details.models?.length || 0) > 0 && <button onClick={() => {
          if (withVariableReset) {
            const newAvailable = getAvailableVariables([]);
            const currentVar = details.conditions?.[0]?.variable_display_name;
            const varUpdates: any = {
              models: []
            };
            if (currentVar && !newAvailable.includes(currentVar) && newAvailable.length > 0) {
              const newVar = newAvailable[0];
              const config = VARIABLE_THRESHOLDS[newVar];
              if (config) {
                const conditions = [...(details.conditions || [])];
                conditions[0] = {
                  ...conditions[0],
                  variable_display_name: newVar,
                  unit: config.unit,
                  operator: config.operator,
                  operator_phrase: config.operator_phrase,
                  threshold: config.values[Math.floor(config.values.length / 2)]
                };
                varUpdates.conditions = conditions;
              }
            }
            updateDetails(varUpdates);
          } else {
            updateDetails({
              models: []
            });
          }
        }} className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.06] transition-colors border-b border-slate-700/40 text-slate-500" data-id="element-255">
                <X size={12} data-id="element-256" />
                <span data-id="element-257">Clear selection</span>
              </button>}
            {MODELS.map(model => {
          const selected = (details.models || []).includes(model);
          return <button key={model} onClick={() => {
            const current = details.models || [];
            const updated = selected ? current.filter((m: string) => m !== model) : [...current, model];
            if (withVariableReset) {
              const newAvailable = getAvailableVariables(updated);
              const currentVar = details.conditions?.[0]?.variable_display_name;
              const varUpdates: any = {
                models: updated
              };
              if (currentVar && !newAvailable.includes(currentVar) && newAvailable.length > 0) {
                const newVar = newAvailable[0];
                const config = VARIABLE_THRESHOLDS[newVar];
                if (config) {
                  const conditions = [...(details.conditions || [])];
                  conditions[0] = {
                    ...conditions[0],
                    variable_display_name: newVar,
                    unit: config.unit,
                    operator: config.operator,
                    operator_phrase: config.operator_phrase,
                    threshold: config.values[Math.floor(config.values.length / 2)]
                  };
                  varUpdates.conditions = conditions;
                }
              }
              updateDetails(varUpdates);
            } else {
              updateDetails({
                models: updated
              });
            }
          }} className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.06] transition-colors" data-id="element-258">
                  <div className={cn('w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors', selected ? 'bg-blue-500 border-blue-500' : 'border-slate-600 bg-transparent')} data-id="element-259">
                    {selected && <Check size={10} className="text-white" data-id="element-260" />}
                  </div>
                  <span className={selected ? 'text-white' : 'text-slate-400'} data-id="element-261">
                    {model}
                  </span>
                </button>;
        })}
          </div>
        </div>}
    </div>;
  // Shared locations trigger + dropdown
  const renderLocationsDropdown = () => <div ref={locationsRef} className="relative" data-id="element-262">
      <button onClick={() => {
      setLocationsOpen(!locationsOpen);
      if (locationsOpen) setLocationSearch('');
    }} className="w-full h-10 pl-10 pr-8 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-left text-slate-100 focus:outline-none focus:ring-2 focus:ring-pink-500/30 transition-colors hover:border-slate-600" data-id="element-263">
        {(details.locations?.length || 0) > 0 ? `${details.locations!.length} location${details.locations!.length > 1 ? 's' : ''}` : 'All locations'}
      </button>
      <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-264" />
      <ChevronDown size={14} className={cn('absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform', locationsOpen && 'rotate-180')} data-id="element-265" />
      {locationsOpen && <LocationsDropdownContent details={details} updateDetails={updateDetails} locationSearch={locationSearch} setLocationSearch={setLocationSearch} data-id="element-266" />}
    </div>;
  return <div className="space-y-8" data-id="element-267">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-268">
        {/* Configuration Form */}
        <div className="lg:col-span-2 space-y-6" data-id="element-269">
          <Card className="p-6 bg-slate-800/40 border-slate-700/50" data-id="element-270">
            <h3 className="text-lg font-semibold text-white mb-6" data-id="element-271">
              Configure Rule
            </h3>

            {/* Data Availability Form */}
            {preset.notification_type === 'data_availability' && <div className="space-y-6" data-id="element-272">
                <div className="space-y-3" data-id="element-273">
                  <label className="text-sm font-medium text-slate-300" data-id="element-274">
                    Model Scope
                  </label>
                  <div ref={modelScopeRef} className="relative" data-id="element-275">
                    <button onClick={() => setModelScopeOpen(!modelScopeOpen)} className="w-full h-10 pl-10 pr-8 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-left text-slate-100 focus:outline-none focus:ring-2 focus:ring-pink-500/30 transition-colors hover:border-slate-600" data-id="element-276">
                      {(details.model_scope?.length || 0) === 0 ? 'Select models…' : (details.model_scope?.length || 0) === MODELS.length ? 'All models' : `${details.model_scope!.length} of ${MODELS.length} models`}
                    </button>
                    <BarChart2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-277" />
                    <ChevronDown size={14} className={cn('absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform', modelScopeOpen && 'rotate-180')} data-id="element-278" />
                    {modelScopeOpen && <div className="absolute z-50 top-full mt-1 left-0 right-0 rounded-xl bg-[#1E293B] border border-slate-700/60 shadow-xl shadow-black/40 overflow-hidden" data-id="element-279">
                        <div className="max-h-64 overflow-y-auto py-1" data-id="element-280">
                          {(details.model_scope?.length || 0) > 0 && <button onClick={() => updateDetails({
                      model_scope: [],
                      mode: 'any'
                    })} className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.06] transition-colors border-b border-slate-700/40 text-slate-500" data-id="element-281">
                              <X size={12} data-id="element-282" />
                              <span data-id="element-283">Clear selection</span>
                            </button>}
                          <button onClick={() => {
                      const allSelected = (details.model_scope?.length || 0) === MODELS.length;
                      updateDetails({
                        model_scope: allSelected ? [] : [...MODELS],
                        mode: allSelected ? 'any' : 'all'
                      });
                    }} className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-white/[0.06] transition-colors border-b border-slate-700/40" data-id="element-284">
                            <div className={cn('w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors', (details.model_scope?.length || 0) === MODELS.length ? 'bg-blue-500 border-blue-500' : 'border-slate-600 bg-transparent')} data-id="element-285">
                              {(details.model_scope?.length || 0) === MODELS.length && <Check size={10} className="text-white" data-id="element-286" />}
                            </div>
                            <span className="text-white font-medium" data-id="element-287">
                              All models
                            </span>
                            <span className="ml-auto text-[10px] text-slate-500" data-id="element-288">
                              Trigger when all update
                            </span>
                          </button>
                          {MODELS.map(model => {
                      const selected = (details.model_scope || []).includes(model);
                      return <button key={model} onClick={() => {
                        const current = details.model_scope || [];
                        const updated = selected ? current.filter((m: string) => m !== model) : [...current, model];
                        updateDetails({
                          model_scope: updated,
                          mode: updated.length === MODELS.length ? 'all' : 'any'
                        });
                      }} className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.06] transition-colors" data-id="element-289">
                                <div className={cn('w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors', selected ? 'bg-blue-500 border-blue-500' : 'border-slate-600 bg-transparent')} data-id="element-290">
                                  {selected && <Check size={10} className="text-white" data-id="element-291" />}
                                </div>
                                <span className={selected ? 'text-white' : 'text-slate-400'} data-id="element-292">
                                  {model}
                                </span>
                              </button>;
                    })}
                        </div>
                      </div>}
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-293">
                    Select "All models" to trigger when every model updates, or
                    pick specific models to trigger on any of them.
                  </p>
                </div>

                {/* Trigger & Expiration Section */}
                <div className="pt-4 border-t border-slate-700/50 space-y-5" data-id="element-294">
                  <div className="flex items-center justify-between" data-id="element-295">
                    <label className="text-sm font-medium text-slate-300" data-id="element-296">
                      Trigger
                    </label>
                    <div ref={triggerRef} className="relative" data-id="element-297">
                      <button onClick={() => setTriggerOpen(!triggerOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1E293B] border border-slate-700/60 text-sm text-slate-100 hover:border-slate-600 transition-colors" data-id="element-298">
                        <Repeat size={12} className="text-slate-400" data-id="element-299" />
                        {details.trigger_frequency === 'once' ? 'Only once' : 'Every time'}
                        <ChevronDown size={12} className={cn('text-slate-400 transition-transform', triggerOpen && 'rotate-180')} data-id="element-300" />
                      </button>
                      {triggerOpen && <div className="absolute z-50 top-full mt-1 right-0 w-44 rounded-xl bg-[#1E293B] border border-slate-700/60 shadow-xl shadow-black/40 overflow-hidden" data-id="element-301">
                          {[{
                      value: 'every_time',
                      label: 'Every time'
                    }, {
                      value: 'once',
                      label: 'Only once'
                    }].map(opt => <button key={opt.value} onClick={() => {
                      updateDetails({
                        trigger_frequency: opt.value
                      });
                      setTriggerOpen(false);
                    }} className={cn('w-full px-3 py-2 text-left text-sm hover:bg-white/[0.06] transition-colors flex items-center justify-between', details.trigger_frequency === opt.value ? 'text-white' : 'text-slate-400')} data-id="element-302">
                              {opt.label}
                              {details.trigger_frequency === opt.value && <Check size={12} className="text-blue-400" data-id="element-303" />}
                            </button>)}
                        </div>}
                    </div>
                  </div>

                  <div className="space-y-4" data-id="element-304">
                    <div className="flex items-center justify-between" data-id="element-305">
                      <label className="text-sm font-medium text-slate-300" data-id="element-306">
                        Expiration
                      </label>
                      {!details.open_ended && details.expires_at && <span className="text-xs text-slate-500 font-mono" data-id="element-307">
                          {new Date(details.expires_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}{' '}
                          at{' '}
                          {new Date(details.expires_at).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    })}
                        </span>}
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-xl bg-[#1E293B]/60 border border-slate-700/40" data-id="element-308">
                      <span className="text-sm text-slate-300" data-id="element-309">
                        Open-ended alert
                      </span>
                      <button onClick={() => {
                    const isCurrentlyOn = details.open_ended !== false;
                    const next = !isCurrentlyOn;
                    updateDetails({
                      open_ended: next,
                      ...(next ? {
                        expires_at: null
                      } : {
                        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                      })
                    });
                  }} className={cn('relative w-10 h-[22px] rounded-full transition-colors', details.open_ended !== false ? 'bg-blue-500' : 'bg-slate-600')} data-id="element-310">
                        <div className={cn('absolute top-[3px] w-4 h-4 rounded-full bg-white transition-transform', details.open_ended !== false ? 'left-[22px]' : 'left-[3px]')} data-id="element-311" />
                      </button>
                    </div>

                    {details.open_ended === false && <div className="space-y-3" data-id="element-312">
                        <div className="flex flex-wrap gap-2" data-id="element-313">
                          {[{
                      label: '1 day',
                      days: 1
                    }, {
                      label: '7 days',
                      days: 7
                    }, {
                      label: '30 days',
                      days: 30
                    }, {
                      label: '90 days',
                      days: 90
                    }].map(opt => {
                      const target = new Date(Date.now() + opt.days * 24 * 60 * 60 * 1000);
                      target.setHours(23, 59, 0, 0);
                      const isActive = details.expires_at && Math.abs(new Date(details.expires_at).getTime() - target.getTime()) < 2 * 60 * 60 * 1000;
                      return <button key={opt.days} onClick={() => updateDetails({
                        expires_at: target.toISOString()
                      })} className={cn('px-3 py-1.5 rounded-lg text-xs font-medium border transition-all', isActive ? 'bg-blue-500/15 border-blue-500/50 text-blue-300' : 'bg-slate-900/50 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-300')} data-id="element-314">
                                {opt.label}
                              </button>;
                    })}
                        </div>

                        <div className="grid grid-cols-2 gap-3" data-id="element-315">
                          <div ref={calendarRef} className="relative" data-id="element-316">
                            <button onClick={() => {
                        if (!calendarOpen && details.expires_at) {
                          setCalendarMonth(new Date(details.expires_at));
                        }
                        setCalendarOpen(!calendarOpen);
                      }} className="w-full h-10 pl-10 pr-8 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-left text-slate-100 focus:outline-none focus:ring-2 focus:ring-pink-500/30 transition-colors hover:border-slate-600" data-id="element-317">
                              {details.expires_at ? new Date(details.expires_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        }) : 'Select date…'}
                            </button>
                            <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-318" />
                            <ChevronDown size={14} className={cn('absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform', calendarOpen && 'rotate-180')} data-id="element-319" />
                            {calendarOpen && <div className="absolute z-50 top-full mt-1 left-0 w-[280px] rounded-xl bg-[#1E293B] border border-slate-700/60 shadow-xl shadow-black/40 p-3" data-id="element-320">
                                <div className="flex items-center justify-between mb-3" data-id="element-321">
                                  <button onClick={() => {
                            const prev = new Date(calendarMonth);
                            prev.setMonth(prev.getMonth() - 1);
                            setCalendarMonth(prev);
                          }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors" data-id="element-322">
                                    <ChevronLeft size={14} data-id="element-323" />
                                  </button>
                                  <span className="text-sm font-semibold text-slate-100" data-id="element-324">
                                    {calendarMonth.toLocaleDateString('en-US', {
                              month: 'long',
                              year: 'numeric'
                            })}
                                  </span>
                                  <button onClick={() => {
                            const next = new Date(calendarMonth);
                            next.setMonth(next.getMonth() + 1);
                            setCalendarMonth(next);
                          }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors" data-id="element-325">
                                    <ChevronRight size={14} data-id="element-326" />
                                  </button>
                                </div>
                                <div className="grid grid-cols-7 mb-1" data-id="element-327">
                                  {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => <div key={d} className="text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wider py-1.5" data-id="element-328">
                                      {d}
                                    </div>)}
                                </div>
                                <div className="grid grid-cols-7 gap-0.5" data-id="element-329">
                                  {(() => {
                            const year = calendarMonth.getFullYear();
                            const month = calendarMonth.getMonth();
                            const firstDay = new Date(year, month, 1);
                            const lastDay = new Date(year, month + 1, 0);
                            let startOffset = firstDay.getDay() - 1;
                            if (startOffset < 0) startOffset = 6;
                            const days: React.ReactNode[] = [];
                            for (let i = 0; i < startOffset; i++) {
                              days.push(<div key={`empty-${i}`} data-id="element-330" />);
                            }
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const selectedDate = details.expires_at ? new Date(details.expires_at) : null;
                            for (let d = 1; d <= lastDay.getDate(); d++) {
                              const date = new Date(year, month, d);
                              const isPast = date < today;
                              const isSelected = selectedDate && selectedDate.getDate() === d && selectedDate.getMonth() === month && selectedDate.getFullYear() === year;
                              const isToday = today.getDate() === d && today.getMonth() === month && today.getFullYear() === year;
                              days.push(<button key={d} disabled={isPast} onClick={() => {
                                const current = details.expires_at ? new Date(details.expires_at) : new Date();
                                current.setFullYear(year, month, d);
                                if (!details.expires_at) current.setHours(23, 59, 0, 0);
                                updateDetails({
                                  expires_at: current.toISOString()
                                });
                                setCalendarOpen(false);
                              }} className={cn('w-[34px] h-[34px] mx-auto flex items-center justify-center rounded-lg text-xs font-medium transition-all', isPast ? 'text-slate-700 cursor-not-allowed' : isSelected ? 'bg-blue-500 text-white shadow-[0_0_8px_rgba(59,130,246,0.3)]' : isToday ? 'text-blue-400 ring-1 ring-blue-500/40 hover:bg-blue-500/15' : 'text-slate-300 hover:bg-white/[0.08] hover:text-white')} data-id="element-331">
                                          {d}
                                        </button>);
                            }
                            return days;
                          })()}
                                </div>
                                <div className="mt-2 pt-2 border-t border-slate-700/40 flex justify-between" data-id="element-332">
                                  <button onClick={() => setCalendarMonth(new Date())} className="text-[11px] text-slate-500 hover:text-blue-400 transition-colors" data-id="element-333">
                                    Today
                                  </button>
                                  <button onClick={() => setCalendarOpen(false)} className="text-[11px] text-slate-500 hover:text-white transition-colors" data-id="element-334">
                                    Close
                                  </button>
                                </div>
                              </div>}
                          </div>
                          <div className="relative" data-id="element-335">
                            <input type="time" value={details.expires_at ? new Date(details.expires_at).toTimeString().slice(0, 5) : '23:59'} onChange={e => {
                        const current = details.expires_at ? new Date(details.expires_at) : new Date();
                        const [h, min] = e.target.value.split(':');
                        current.setHours(+h, +min, 0, 0);
                        updateDetails({
                          expires_at: current.toISOString()
                        });
                      }} className="alert-date-input w-full h-10 pl-10 pr-3 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-pink-500/30 [color-scheme:dark]" data-id="element-336" />
                            <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-337" />
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}

            {/* Threshold Form */}
            {preset.notification_type === 'threshold' && details.conditions && details.conditions.length > 0 && <div className="space-y-6" data-id="element-338">
                  <div className="space-y-3" data-id="element-339">
                    <label className="text-sm font-medium text-slate-300" data-id="element-340">
                      Scope
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-341">
                      {/* Models dropdown — power uses single model, weather uses MODELS */}
                      {preset.id === 'market-power' ? <div className="relative" data-id="element-342">
                          <div className="w-full h-10 pl-10 pr-3 rounded-xl bg-[#1E293B]/60 border border-slate-700/40 flex items-center text-sm text-slate-300" data-id="element-343">
                            {POWER_MODELS[0]}
                          </div>
                          <BarChart2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-344" />
                        </div> : renderModelsDropdown(true)}

                      {/* Locations/Zones dropdown — power uses single zone */}
                      {preset.id === 'market-power' ? <div className="relative" data-id="element-345">
                          <div className="w-full h-10 pl-10 pr-3 rounded-xl bg-[#1E293B]/60 border border-slate-700/40 flex items-center text-sm text-slate-300" data-id="element-346">
                            DE-LU (Germany)
                          </div>
                          <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-347" />
                        </div> : renderLocationsDropdown()}
                    </div>
                    <p className="text-xs text-slate-500" data-id="element-348">
                      {preset.id === 'market-power' ? 'Select power models and European market zones.' : 'Available variables depend on the selected models.'}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-349">
                    <div className="space-y-3" data-id="element-350">
                      <label className="text-sm font-medium text-slate-300" data-id="element-351">
                        Variable
                      </label>
                      <div className="relative" data-id="element-352">
                        <select value={details.conditions[0].variable_display_name} onChange={e => {
                    const varName = e.target.value;
                    if (preset.id === 'market-power') {
                      const pv = POWER_VARIABLES.find(v => v.name === varName);
                      if (pv) {
                        updateCondition(0, {
                          variable_display_name: varName,
                          unit: pv.unit,
                          operator: pv.operator,
                          operator_phrase: pv.operator_phrase,
                          threshold: pv.values[Math.floor(pv.values.length / 2)]
                        });
                      }
                    } else {
                      const config = VARIABLE_THRESHOLDS[varName];
                      updateCondition(0, {
                        variable_display_name: varName,
                        ...(config && {
                          unit: config.unit,
                          operator: config.operator,
                          operator_phrase: config.operator_phrase,
                          threshold: config.values[Math.floor(config.values.length / 2)]
                        })
                      });
                    }
                  }} className="jua-select pl-10 pr-8" data-id="element-353">
                          {preset.id === 'market-power' ? POWER_VARIABLES.map(pv => <option key={pv.name} value={pv.name} data-id="element-354">
                                  {pv.name}
                                </option>) : getAvailableVariables(details.models || []).map(varName => <option key={varName} value={varName} data-id="element-355">
                                    {varName}
                                  </option>)}
                        </select>
                        <Activity size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-356" />
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-357" />
                      </div>
                    </div>
                    <div className="space-y-3" data-id="element-358">
                      <label className="text-sm font-medium text-slate-300" data-id="element-359">
                        Operator
                      </label>
                      <div className="relative" data-id="element-360">
                        <select value={details.conditions[0].operator} onChange={e => {
                    const op = e.target.value;
                    const phrases: Record<string, string> = {
                      '>': 'above',
                      '>=': 'at or above',
                      '<': 'below',
                      '<=': 'at or below',
                      '=': 'equal to',
                      '!=': 'not equal to'
                    };
                    updateCondition(0, {
                      operator: op,
                      operator_phrase: phrases[op] || op
                    });
                  }} className="jua-select pl-3 pr-8" data-id="element-361">
                          <option value=">" data-id="element-362">Greater than (&gt;)</option>
                          <option value=">=" data-id="element-363">Greater or equal (≥)</option>
                          <option value="<" data-id="element-364">Less than (&lt;)</option>
                          <option value="<=" data-id="element-365">Less or equal (≤)</option>
                          <option value="=" data-id="element-366">Equal to (=)</option>
                          <option value="!=" data-id="element-367">Not equal to (≠)</option>
                        </select>
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-368" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-369">
                    <div className="space-y-3" data-id="element-370">
                      <label className="text-sm font-medium text-slate-300" data-id="element-371">
                        Threshold Value
                      </label>
                      <div className="relative" data-id="element-372">
                        <input type="text" inputMode="decimal" value={details.conditions[0].threshold ?? ''} onChange={e => {
                    const raw = e.target.value;
                    if (raw === '' || raw === '-') {
                      updateCondition(0, {
                        threshold: raw as any
                      });
                      return;
                    }
                    const num = parseFloat(raw);
                    if (!isNaN(num)) {
                      updateCondition(0, {
                        threshold: num
                      });
                    }
                  }} className="w-full h-10 pl-10 pr-16 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-pink-500/30 hover:border-slate-600 transition-colors" data-id="element-373" />
                        <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-374" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-mono pointer-events-none" data-id="element-375">
                          {details.conditions[0].unit}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3" data-id="element-376">
                      <label className="text-sm font-medium text-slate-300" data-id="element-377">
                        Unit
                      </label>
                      <div className="h-10 px-3 rounded-xl bg-[#1E293B]/60 border border-slate-700/40 flex items-center text-sm text-slate-300 font-mono" data-id="element-378">
                        {details.conditions[0].unit}
                      </div>
                      <p className="text-xs text-slate-500" data-id="element-379">
                        Auto-set from variable
                      </p>
                    </div>
                  </div>
                </div>}

            {/* Correction Form */}
            {preset.notification_type === 'correction' && details.conditions && details.conditions.length > 0 && <div className="space-y-6" data-id="element-380">
                  <div className="space-y-3" data-id="element-381">
                    <label className="text-sm font-medium text-slate-300" data-id="element-382">
                      Scope
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-383">
                      {renderModelsDropdown(false)}
                      {renderLocationsDropdown()}
                    </div>
                  </div>

                  {/* Trigger Type */}
                  <div className="space-y-3" data-id="element-384">
                    <label className="text-sm font-medium text-slate-300" data-id="element-385">
                      Trigger
                    </label>
                    <div ref={correctionTriggerRef} className="relative" data-id="element-386">
                      <button onClick={() => setCorrectionTriggerOpen(!correctionTriggerOpen)} className="w-full h-10 pl-10 pr-8 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-left text-slate-100 focus:outline-none focus:ring-2 focus:ring-pink-500/30 transition-colors hover:border-slate-600" data-id="element-387">
                        {details.trigger_type === 'crossing_up' ? 'Crossing Up' : details.trigger_type === 'crossing_down' ? 'Crossing Down' : 'Crossing'}
                      </button>
                      <ArrowUpDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-388" />
                      <ChevronDown size={14} className={cn('absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform', correctionTriggerOpen && 'rotate-180')} data-id="element-389" />
                      {correctionTriggerOpen && <div className="absolute z-50 top-full mt-1 left-0 right-0 rounded-xl bg-[#1E293B] border border-slate-700/60 shadow-xl shadow-black/40 overflow-hidden" data-id="element-390">
                          <div className="py-1" data-id="element-391">
                            {[{
                      value: 'crossing',
                      label: 'Crossing',
                      icon: ArrowUpDown,
                      desc: 'Triggers when value crosses threshold in either direction'
                    }, {
                      value: 'crossing_up',
                      label: 'Crossing Up',
                      icon: TrendingUp,
                      desc: 'Triggers when value crosses above threshold'
                    }, {
                      value: 'crossing_down',
                      label: 'Crossing Down',
                      icon: TrendingDown,
                      desc: 'Triggers when value crosses below threshold'
                    }].map(opt => {
                      const isSelected = (details.trigger_type || 'crossing') === opt.value;
                      const Icon = opt.icon;
                      return <button key={opt.value} onClick={() => {
                        updateDetails({
                          trigger_type: opt.value
                        });
                        setCorrectionTriggerOpen(false);
                      }} className={cn('w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/[0.06] transition-colors', isSelected ? 'bg-white/[0.04]' : '')} data-id="element-392">
                                  <Icon size={14} className={isSelected ? 'text-blue-400' : 'text-slate-500'} data-id="element-393" />
                                  <div className="flex-1 min-w-0" data-id="element-394">
                                    <div className={cn('text-sm', isSelected ? 'text-white' : 'text-slate-300')} data-id="element-395">
                                      {opt.label}
                                    </div>
                                    <div className="text-[10px] text-slate-500" data-id="element-396">
                                      {opt.desc}
                                    </div>
                                  </div>
                                  {isSelected && <Check size={12} className="text-blue-400 flex-shrink-0" data-id="element-397" />}
                                </button>;
                    })}
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-398">
                    <label className="text-sm font-medium text-slate-300" data-id="element-399">
                      Variable to Monitor
                    </label>
                    <div className="relative" data-id="element-400">
                      <select value={details.conditions[0].variable_display_name} onChange={e => updateCondition(0, {
                  variable_display_name: e.target.value
                })} className="jua-select pl-10 pr-8" data-id="element-401">
                        <option value="Wind Generation" data-id="element-402">Wind Generation</option>
                        <option value="Solar Generation" data-id="element-403">
                          Solar Generation
                        </option>
                        <option value="Temperature" data-id="element-404">Temperature</option>
                      </select>
                      <Activity size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-405" />
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-406" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-407">
                    <div className="space-y-3" data-id="element-408">
                      <label className="text-sm font-medium text-slate-300" data-id="element-409">
                        Change Threshold
                      </label>
                      <Input type="number" value={details.conditions[0].threshold} onChange={e => updateCondition(0, {
                  threshold: parseFloat(e.target.value) || 0
                })} icon={<Hash size={14} data-id="element-411" />} data-id="element-410" />
                    </div>
                    <div className="space-y-3" data-id="element-412">
                      <label className="text-sm font-medium text-slate-300" data-id="element-413">
                        Unit
                      </label>
                      <div className="relative" data-id="element-414">
                        <select value={details.conditions[0].unit} onChange={e => updateCondition(0, {
                    unit: e.target.value
                  })} className="jua-select pl-3 pr-8" data-id="element-415">
                          <option value="%" data-id="element-416">% (Percent)</option>
                          <option value="GW" data-id="element-417">GW (Absolute)</option>
                          <option value="MW" data-id="element-418">MW (Absolute)</option>
                        </select>
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-419" />
                      </div>
                    </div>
                  </div>
                </div>}

            {/* Model Divergence Form */}
            {preset.notification_type === 'model_divergence' && details.conditions && details.conditions.length > 0 && <div className="space-y-6" data-id="element-420">
                  {/* Scope — side by side like other flows */}
                  <div className="space-y-3" data-id="element-421">
                    <label className="text-sm font-medium text-slate-300" data-id="element-422">
                      Scope
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-423">
                      {renderModelsDropdown(false)}
                      {renderLocationsDropdown()}
                    </div>
                    <p className="text-xs text-slate-500" data-id="element-424">
                      Select at least 2 models to compare. Leave empty to
                      compare all available models.
                    </p>
                  </div>

                  {/* Variable */}
                  <div className="space-y-3" data-id="element-425">
                    <label className="text-sm font-medium text-slate-300" data-id="element-426">
                      Variable to Monitor
                    </label>
                    <div className="relative" data-id="element-427">
                      <select value={details.conditions[0].variable_display_name} onChange={e => updateCondition(0, {
                  variable_display_name: e.target.value
                })} className="jua-select pl-10 pr-8" data-id="element-428">
                        <option value="Wind Speed" data-id="element-429">Wind Speed</option>
                        <option value="Temperature" data-id="element-430">Temperature</option>
                        <option value="Wind Generation" data-id="element-431">Wind Generation</option>
                        <option value="Solar Generation" data-id="element-432">
                          Solar Generation
                        </option>
                      </select>
                      <Activity size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-433" />
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-434" />
                    </div>
                  </div>

                  {/* Divergence Threshold */}
                  <div className="grid grid-cols-2 gap-4" data-id="element-435">
                    <div className="space-y-3" data-id="element-436">
                      <label className="text-sm font-medium text-slate-300" data-id="element-437">
                        Divergence Threshold
                      </label>
                      <Input type="number" value={details.conditions[0].threshold} onChange={e => updateCondition(0, {
                  threshold: parseFloat(e.target.value) || 0
                })} icon={<Hash size={14} data-id="element-439" />} data-id="element-438" />
                    </div>
                    <div className="space-y-3" data-id="element-440">
                      <label className="text-sm font-medium text-slate-300" data-id="element-441">
                        Unit
                      </label>
                      <div className="relative" data-id="element-442">
                        <select value={details.conditions[0].unit} onChange={e => updateCondition(0, {
                    unit: e.target.value
                  })} className="jua-select pl-3 pr-8" data-id="element-443">
                          <option value="%" data-id="element-444">% (Relative spread)</option>
                          <option value="m/s" data-id="element-445">m/s (Absolute)</option>
                          <option value="°C" data-id="element-446">°C (Absolute)</option>
                          <option value="GW" data-id="element-447">GW (Absolute)</option>
                        </select>
                        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-448" />
                      </div>
                    </div>
                  </div>
                </div>}

            {/* Advanced Accordion */}
            <div className={cn('mt-4 border border-slate-700/50 bg-slate-800/40', advancedOpen ? 'rounded-t-xl' : 'rounded-xl')} data-id="element-449">
              <button onClick={() => setAdvancedOpen(!advancedOpen)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800/30 transition-colors rounded-xl" data-id="element-450">
                <div className="flex items-center gap-2" data-id="element-451">
                  <Settings2 size={14} className="text-slate-500" data-id="element-452" />
                  <span data-id="element-453">Advanced</span>
                </div>
                <ChevronRight size={14} className={cn('text-slate-500 transition-transform duration-200', advancedOpen && 'rotate-90')} data-id="element-454" />
              </button>
              <div className={cn('transition-all duration-200 ease-in-out', advancedOpen ? 'max-h-[500px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden')} data-id="element-455">
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-slate-700/50" data-id="element-456">
                  <label className="text-sm font-medium text-slate-300" data-id="element-457">
                    Linked Workspace
                  </label>
                  <div ref={workspaceDropdownRef} className="relative pb-2" data-id="element-458">
                    <button onClick={() => setWorkspaceDropdownOpen(!workspaceDropdownOpen)} className="w-full h-10 pl-10 pr-8 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-left text-slate-100 focus:outline-none focus:ring-2 focus:ring-pink-500/30 transition-colors hover:border-slate-600" data-id="element-459">
                      {rule.workspace_id ? workspaces.find(w => w.id === rule.workspace_id)?.name || 'Unknown' : 'None'}
                    </button>
                    <Layout size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-460" />
                    <ChevronDown size={14} className={cn('absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform', workspaceDropdownOpen && 'rotate-180')} data-id="element-461" />
                    {workspaceDropdownOpen && <div className="absolute z-50 top-full mt-1 left-0 right-0 rounded-xl bg-[#1E293B] border border-slate-700/60 shadow-xl shadow-black/40 overflow-hidden" data-id="element-462">
                        <div className="max-h-80 overflow-y-auto py-1" data-id="element-463">
                          {/* None option */}
                          <button onClick={() => {
                        onChange({
                          workspace_id: undefined,
                          workspace_name: undefined
                        });
                        setWorkspaceDropdownOpen(false);
                      }} className={cn('w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-white/[0.06] transition-colors border-b border-slate-700/40', !rule.workspace_id ? 'text-white' : 'text-slate-400')} data-id="element-464">
                            <X size={12} className="text-slate-500 flex-shrink-0" data-id="element-465" />
                            <span data-id="element-466">None</span>
                            {!rule.workspace_id && <Check size={12} className="ml-auto text-blue-400 flex-shrink-0" data-id="element-467" />}
                          </button>
                          {/* Pinned workspaces first */}
                          {workspaces.filter(ws => ws.pinned).length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-468">
                              Pinned
                            </div>}
                          {workspaces.filter(ws => ws.pinned).map(ws => {
                        const isSelected = rule.workspace_id === ws.id;
                        return <button key={ws.id} onClick={() => {
                          onChange({
                            workspace_id: ws.id,
                            workspace_name: ws.name
                          });
                          setWorkspaceDropdownOpen(false);
                        }} className={cn('w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.06] transition-colors', isSelected ? 'bg-blue-500/10' : '')} data-id="element-469">
                                  <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-470">
                                    <Pin size={10} className="text-blue-400" data-id="element-471" />
                                    {ws.favorited && <Star size={10} className="text-amber-400 fill-amber-400" data-id="element-472" />}
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-473">
                                    <div className={cn('text-sm truncate', isSelected ? 'text-white' : 'text-slate-300')} data-id="element-474">
                                      {ws.name}
                                    </div>
                                    <div className="text-[10px] text-slate-500 truncate" data-id="element-475">
                                      {ws.description}
                                    </div>
                                  </div>
                                  {isSelected && <Check size={12} className="text-blue-400 flex-shrink-0" data-id="element-476" />}
                                </button>;
                      })}
                          {/* Other workspaces */}
                          {workspaces.filter(ws => !ws.pinned).length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wider" data-id="element-477">
                              {workspaces.filter(ws => ws.pinned).length > 0 ? 'Other' : 'Workspaces'}
                            </div>}
                          {workspaces.filter(ws => !ws.pinned).map(ws => {
                        const isSelected = rule.workspace_id === ws.id;
                        return <button key={ws.id} onClick={() => {
                          onChange({
                            workspace_id: ws.id,
                            workspace_name: ws.name
                          });
                          setWorkspaceDropdownOpen(false);
                        }} className={cn('w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.06] transition-colors', isSelected ? 'bg-blue-500/10' : '')} data-id="element-478">
                                  <div className="flex items-center gap-1.5 flex-shrink-0 w-5" data-id="element-479">
                                    {ws.favorited && <Star size={10} className="text-amber-400 fill-amber-400" data-id="element-480" />}
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-481">
                                    <div className={cn('text-sm truncate', isSelected ? 'text-white' : 'text-slate-300')} data-id="element-482">
                                      {ws.name}
                                    </div>
                                    <div className="text-[10px] text-slate-500 truncate" data-id="element-483">
                                      {ws.description}
                                    </div>
                                  </div>
                                  {isSelected && <Check size={12} className="text-blue-400 flex-shrink-0" data-id="element-484" />}
                                </button>;
                      })}
                        </div>
                      </div>}
                  </div>
                  <p className="text-xs text-slate-500" data-id="element-485">
                    When this alert triggers, clicking the notification will
                    navigate you to this workspace.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Helper / Preview Panel */}
        <div className="space-y-6" data-id="element-486">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4" data-id="element-487">
            <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center gap-2" data-id="element-488">
              <Info size={14} data-id="element-489" />
              About this rule
            </h4>
            <p className="text-xs text-blue-300/80 leading-relaxed" data-id="element-490">
              {preset.description}
            </p>
          </div>

          <div className="space-y-2" data-id="element-491">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-492">
              Configuration Checklist
            </h4>
            <ul className="space-y-2" data-id="element-493">
              {preset.notification_type === 'data_availability' && <>
                  <li className="flex items-center gap-2 text-sm" data-id="element-494">
                    <div className={cn('w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0', (details.model_scope?.length || 0) > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700/50 text-slate-600')} data-id="element-495">
                      <Check size={10} data-id="element-496" />
                    </div>
                    <span className={(details.model_scope?.length || 0) > 0 ? 'text-slate-200' : 'text-slate-500'} data-id="element-497">
                      Model scope
                    </span>
                  </li>
                  <li className="flex items-center gap-2 text-sm" data-id="element-498">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500/20 text-emerald-400" data-id="element-499">
                      <Check size={10} data-id="element-500" />
                    </div>
                    <span className="text-slate-200" data-id="element-501">Trigger frequency</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm" data-id="element-502">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500/20 text-emerald-400" data-id="element-503">
                      <Check size={10} data-id="element-504" />
                    </div>
                    <span className="text-slate-200" data-id="element-505">
                      Expiration{' '}
                      {details.open_ended !== false ? '(open-ended)' : '(set)'}
                    </span>
                  </li>
                </>}
              {preset.notification_type !== 'data_availability' && preset.required_inputs.map((req, i) => <li key={i} className="flex items-center gap-2 text-sm text-slate-300" data-id="element-506">
                    <div className="w-1.5 h-1.5 rounded-full bg-pink-500" data-id="element-507" />
                    {req}
                  </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}